import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import CloneComponent from "./common-components/clone-component";

interface AccordionFormProps {
  title: string;
  tooltipContent: string;
  form: React.ReactNode;
  isActive?: boolean;
}

export interface AccordionFormRef {
  scrollIntoView: () => void;
  expand: () => void;
}

const AccordionForm = forwardRef<AccordionFormRef, AccordionFormProps>(
  ({ title, tooltipContent, form, isActive }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const accordionRef = useRef<HTMLDivElement>(null);
    const {palletTypes}=useSelector((state:RootState)=>state.pallets)
    useImperativeHandle(ref, () => ({
      scrollIntoView: () => {
        accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      },
      expand: () => {
        setIsExpanded(true);
      }
    }));

    return (
      <div ref={accordionRef} className={` `}>
        <Accordion
          type="single"
          collapsible
          className="w-full px-2 lg:px-auto lg:x-space"
          value={isExpanded ? "item-1" : ""}
          onValueChange={(value) => setIsExpanded(value === "item-1")}
        >
          <AccordionItem value="item-1">
            <AccordionTrigger className="border data-[state=open]:border-b-0 p-2 flex-center justify-start gap-2 text-left border-border-style-1 rounded-md data-[state=open]:rounded-b-none data-[state=open]:text-blue">
              <p className="text-left font-semibold">{title}</p>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p>
                      <Info size={20} className="text-[#AAB3C0]" />
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{tooltipContent}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <CloneComponent title={title}/>
            </AccordionTrigger>
            <AccordionContent className="p-0 border border-t-0 border-border-style-1 rounded-b-md">
              {form}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }
);

AccordionForm.displayName = "AccordionForm";

export default AccordionForm;